import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import { of } from "rxjs";
import { catchError, debounceTime, filter, map, mergeMap, switchMap, withLatestFrom } from "rxjs/operators";

import { PurchaseOrderService } from "../../services/api/purchase-order.service";
import { PurchaseOrderActions } from "../actions/purchase-order.actions";
import { FailState } from "../models/fail-state";
import { PurchaseOrdersState } from "../models/purchase-orders-state";
import { AppState } from "../reducers";
import { PurchaseOrderSelectors } from "../selectors/purchase-order.selectors";

@Injectable()
export class PurchaseOrderEffects {
	// Orders list - cache check
	orders = createEffect(() =>
		this.actions.pipe(
			ofType(PurchaseOrderActions.orders.get),
			withLatestFrom(this.store.select(PurchaseOrderSelectors.purchaseOrders)),
			switchMap(([action, state]) =>
				this.canUseOrdersCache(state)
					? of(PurchaseOrderActions.orders.getSuccess({ orders: state.orders }))
					: of(PurchaseOrderActions.orders.getApi({ consumerId: action.consumerId }))
			)
		)
	);

	// Orders list - API call
	ordersApi = createEffect(() =>
		this.actions.pipe(
			ofType(PurchaseOrderActions.orders.getApi),
			switchMap((action) =>
				this.service.searchOrders({ consumerId: action.consumerId, orderType: "ORDER" }).pipe(
					map((response) => PurchaseOrderActions.orders.getSuccess({ orders: response.items })),
					catchError((error) =>
						of(PurchaseOrderActions.orders.getFail(new FailState(error)))
					)
				)
			)
		)
	);

	// Selected order - always fetch fresh
	selectedOrder = createEffect(() =>
		this.actions.pipe(
			ofType(PurchaseOrderActions.selectedOrder.get),
			switchMap((action) =>
				this.service.getOrder(action.orderId).pipe(
					map((order) => PurchaseOrderActions.selectedOrder.getSuccess({ order })),
					catchError((error) =>
						of(PurchaseOrderActions.selectedOrder.getFail(new FailState(error)))
					)
				)
			)
		)
	);

	// Selected order - WS push / reconcile-triggered refetch
	orderStatusRefetch = createEffect(() =>
		this.actions.pipe(
			ofType(
				PurchaseOrderActions.selectedOrder.wsStatusReceived,
				PurchaseOrderActions.selectedOrder.reconcile
			),
			concatLatestFrom(() => this.store.select(PurchaseOrderSelectors.selectedOrder)),
			filter(([action, selectedOrder]): boolean => {
				if (!selectedOrder) { return false; }
				if ("orderId" in action) { return String(action.orderId) === selectedOrder.id; }
				return true;
			}),
			debounceTime(250),
			switchMap(([, selectedOrder]) =>
				of(PurchaseOrderActions.selectedOrder.get({ orderId: selectedOrder!.id }))
			)
		)
	);

	// Status update
	statusUpdate = createEffect(() =>
		this.actions.pipe(
			ofType(PurchaseOrderActions.status.update),
			mergeMap((action) =>
				this.service
					.updateOrderStatus(action.orderId, {
						newStatus: action.newStatus,
						cancelReason: action.cancelReason
					})
					.pipe(
						map((order) => PurchaseOrderActions.status.updateSuccess({ order })),
						catchError((error) =>
							of(PurchaseOrderActions.status.updateFail(new FailState(error)))
						)
					)
			)
		)
	);

	// Templates list - cache check
	templates = createEffect(() =>
		this.actions.pipe(
			ofType(PurchaseOrderActions.templates.get),
			withLatestFrom(this.store.select(PurchaseOrderSelectors.purchaseOrders)),
			switchMap(([action, state]) =>
				this.canUseTemplatesCache(state)
					? of(PurchaseOrderActions.templates.getSuccess({ templates: state.templates }))
					: of(PurchaseOrderActions.templates.getApi({ consumerId: action.consumerId }))
			)
		)
	);

	// Templates list - API call
	templatesApi = createEffect(() =>
		this.actions.pipe(
			ofType(PurchaseOrderActions.templates.getApi),
			switchMap((action) =>
				this.service.searchOrders({ consumerId: action.consumerId, orderType: "TEMPLATE" }).pipe(
					map((response) => PurchaseOrderActions.templates.getSuccess({ templates: response.items })),
					catchError((error) =>
						of(PurchaseOrderActions.templates.getFail(new FailState(error)))
					)
				)
			)
		)
	);

	constructor(
		private actions: Actions,
		private service: PurchaseOrderService,
		private store: Store<AppState>
	) {}

	private canUseOrdersCache(state: PurchaseOrdersState): boolean {
		return (
			state !== undefined &&
			!_.has(state, "error") &&
			!state.loading &&
			state.hasLoaded === true &&
			_.has(state, "orders") &&
			state.orders !== undefined
		);
	}

	private canUseTemplatesCache(state: PurchaseOrdersState): boolean {
		return (
			state !== undefined &&
			!_.has(state, "error") &&
			!state.loading &&
			state.templatesHasLoaded === true &&
			_.has(state, "templates") &&
			state.templates !== undefined
		);
	}
}
